import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { resultCount } from '@/lib/result-count';

import { MIN_SEARCH_TERM_LENGTH, useDebouncedCallback } from '@/lib/useDebouncedCallback';

import { DangerButton } from '@/components/danger/DangerButton';
import { DataTable, type Column, type SortState } from '@/components/data/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { DraftSchedulePanel } from '@/features/leagues/DraftSchedulePanel';
import {
  ISSUE_LABELS,
  LEAGUE_SIZES,
  SPORT_OPTIONS,
  useLeagueList,
  type LeagueIssue,
  type LeagueListRow,
} from '@/features/leagues/leagues';

/**
 * Filters live in the URL, not in component state.
 *
 * Deliberate: an operator mid-incident needs to paste "the filtered view I am looking
 * at" into a Slack thread, and reload without losing it. It also reduces the brief's
 * "saved filters" to storing URLs rather than inventing a serialisation format.
 */
const searchSchema = z.object({
  q: z.string().optional(),
  sport: z.string().optional(),
  size: z.coerce.number().optional(),
  weekStatus: z.string().optional(),
  seasonStatus: z.string().optional(),
  hasIssues: z.boolean().optional(),
  sort: z.string().default('name'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().default(1),
});

export const Route = createFileRoute('/leagues/')({
  validateSearch: searchSchema,
  component: LeaguesScreen,
});

function LeaguesScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  /**
   * A term below the API's minimum is refused there rather than scanned for, so the
   * request is not sent at all. Derived from the URL (the committed value), not the
   * text box, so it matches what would actually go over the wire.
   */
  const committedTooShort =
    (search.q?.trim().length ?? 0) > 0 &&
    (search.q?.trim().length ?? 0) < MIN_SEARCH_TERM_LENGTH;

  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useLeagueList(
    {
      q: search.q,
      sport: search.sport,
      size: search.size,
      weekStatus: search.weekStatus,
      seasonStatus: search.seasonStatus,
      hasIssues: search.hasIssues,
      sort: search.sort,
      direction: search.direction,
      page: search.page,
    },
    !committedTooShort,
  );

  /*
    The text box is local state; the URL is written on a debounce.

    This search is a collection scan on a ZazzBackend-owned collection — the same
    database the live consumer app reads from — so one request per keystroke is real
    load on production, not just a wasted round trip. The URL stays the source of
    truth so a pasted link and a reload are unchanged.
  */
  const [term, setTerm] = useState(search.q ?? '');
  // The last value this input itself produced. Comparing the URL against it is what
  // separates "the debounce just landed" from "the URL changed underneath us".
  const sentRef = useRef(search.q ?? '');

  // Adjust during render rather than in an effect: an effect would fire a second
  // render pass, and syncing state from props in one is the cascade React warns
  // about. The guard means this only runs on a genuine external change —
  // back/forward, a pasted link, or the Clear button — never on our own commit.
  const urlTerm = search.q ?? '';
  if (urlTerm !== sentRef.current) {
    sentRef.current = urlTerm;
    setTerm(urlTerm);
  }

  const commitTerm = useDebouncedCallback((value: string) => {
    setSearch({ q: value || undefined });
  }, 300);

  const termTooShort = term.trim().length > 0 && term.trim().length < MIN_SEARCH_TERM_LENGTH;

  function setSearch(next: Record<string, unknown>) {
    void navigate({
      search: (previous) => ({ ...previous, ...next, page: (next.page as number) ?? 1 }),
      replace: true,
    });
  }

  const columns: Column<LeagueListRow>[] = [
    {
      id: 'name',
      header: 'League',
      sortable: true,
      width: 'minmax(220px, 1.8fr)',
      mobile: 'primary',
      cell: (league) => (
        <div className="min-w-0">
          <EntityChip
            kind="league"
            id={league.id}
            name={league.name}
            secondary={league.shareCode ?? undefined}
            to={`/leagues/${league.id}`}
          />
          {league.issues.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {league.issues.map((issue) => (
                <IssueBadge key={issue} issue={issue} />
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'sport',
      header: 'Sport',
      width: '110px',
      mobile: 'secondary',
      cell: (league) => <span className="text-tx2 capitalize">{league.sport ?? '—'}</span>,
    },
    {
      id: 'filled',
      // Teams, not people. `leagueSize` is a count of squad slots, so the two halves
      // of this fraction have to be the same unit — they were not, and a league with
      // three-person squads read as "21 / 10".
      header: 'Teams',
      width: '96px',
      mobile: 'secondary',
      cell: (league) => (
        <span
          className="mesh-mono"
          style={{
            // Amber, not red: an unfilled league is a thing to look at, not a danger.
            color:
              league.leagueSize !== null && league.filledTeams < league.leagueSize
                ? 'var(--warn)'
                : 'var(--tx2)',
          }}
        >
          {league.filledTeams} / {league.leagueSize ?? '?'}
        </span>
      ),
    },
    {
      id: 'members',
      header: 'Users',
      width: '80px',
      align: 'right',
      mobile: 'secondary',
      cell: (league) => <span className="mesh-mono text-tx2">{league.memberCount}</span>,
    },
    {
      id: 'week',
      header: 'Week',
      width: '170px',
      mobile: 'secondary',
      cell: (league) => (
        <div className="flex items-center gap-1.5">
          <span className="mesh-mono text-tx3">
            {league.currentWeek === null ? '—' : `W${String(league.currentWeek)}`}
          </span>
          {league.weekStatus && <StateChip state={league.weekStatus} size="sm" />}
        </div>
      ),
    },
    {
      id: 'season',
      header: 'Season',
      width: '120px',
      mobile: 'detail',
      cell: (league) => <StateChip state={league.seasonStatus} size="sm" />,
    },
    {
      id: 'commissioner',
      header: 'Commissioner',
      width: 'minmax(0, 1fr)',
      mobile: 'detail',
      cell: (league) => (
        <span className="text-tx2 truncate">{league.commissioner?.name ?? '—'}</span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      width: '110px',
      sortable: true,
      align: 'right',
      mobile: 'detail',
      cell: (league) => (
        <span className="mesh-mono text-tx3">
          {league.createdAt ? league.createdAt.slice(0, 10) : '—'}
        </span>
      ),
    },
  ];

  const sort: SortState = { columnId: search.sort, direction: search.direction };

  const activeFilterCount = [
    search.sport,
    search.size,
    search.weekStatus,
    search.seasonStatus,
    search.hasIssues ? 'issues' : undefined,
  ].filter(Boolean).length;

  return (
    <RequireCapability capability="leagues.view">
      <div className="flex flex-col gap-3.5">
        <PageHeader
          eyebrow="Leagues"
          title={
            data
              ? resultCount(data.total, data.hasMore, data.rows.length, 'league', 'leagues')
              : 'Leagues'
          }
          meta={
            data ? (
              <span>
                {data.rows.length} shown
                {data.postFiltered && (
                  <span className="text-tx3">
                    {' · '}filtered after fetch, so the total is pre-filter
                  </span>
                )}
              </span>
            ) : undefined
          }
          actions={
            <>
              <LiveDataIndicator
                updatedAt={dataUpdatedAt || undefined}
                isFetching={isFetching}
                onRefresh={() => void refetch()}
              />
              <DangerButton tier={1}>Export CSV</DangerButton>
            </>
          }
        />

        {/*
          Above the list, not below it: the list answers "which league?", this answers
          "what is tonight going to look like?", and the second question is the one
          an operator has when they open this tab the afternoon before a draft night.
        */}
        <DraftSchedulePanel />

        <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                aria-hidden
                className="text-tx3 pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <input
                type="search"
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  sentRef.current = event.target.value;
                  commitTerm(event.target.value);
                }}
                placeholder="Search by name or invite code…"
                aria-label="Search leagues"
                className="border-bd bg-bg-alt text-tx placeholder:text-tx3 focus-visible:border-bd-s h-11 w-full rounded-lg border pr-2.5 pl-8 text-[13px] outline-none"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() =>
                  setSearch({
                    sport: undefined,
                    size: undefined,
                    weekStatus: undefined,
                    seasonStatus: undefined,
                    hasIssues: undefined,
                  })
                }
                className="border-bd text-tx2 hover:bg-elev hover:text-tx flex h-11 flex-none items-center gap-1.5 rounded-lg border px-2.5 text-xs"
              >
                <X size={12} aria-hidden />
                Clear {activeFilterCount}
              </button>
            )}
          </div>

          {/* The API refuses a term this short rather than scanning; say so before it does. */}
          {termTooShort && (
            <p className="text-tx3 text-[11.5px]">
              Keep typing — searches run from {MIN_SEARCH_TERM_LENGTH} characters.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <FilterSelect
              label="Sport"
              value={search.sport ?? ''}
              onChange={(value) => setSearch({ sport: value || undefined })}
              options={SPORT_OPTIONS.map((sport) => ({ value: sport, label: sport }))}
            />
            <FilterSelect
              label="Size"
              value={search.size ? String(search.size) : ''}
              onChange={(value) => setSearch({ size: value ? Number(value) : undefined })}
              options={LEAGUE_SIZES.map((size) => ({
                value: String(size),
                label: `${String(size)} teams`,
              }))}
            />
            <FilterSelect
              label="Week"
              value={search.weekStatus ?? ''}
              onChange={(value) => setSearch({ weekStatus: value || undefined })}
              options={['upcoming', 'active', 'processing', 'complete'].map((status) => ({
                value: status,
                label: status,
              }))}
            />
            <FilterSelect
              label="Season"
              value={search.seasonStatus ?? ''}
              onChange={(value) => setSearch({ seasonStatus: value || undefined })}
              options={[
                { value: 'active', label: 'active' },
                { value: 'completed', label: 'completed' },
                { value: 'none', label: 'no season' },
              ]}
            />
            <button
              type="button"
              onClick={() => setSearch({ hasIssues: search.hasIssues ? undefined : true })}
              aria-pressed={search.hasIssues === true}
              className="font-head flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[11.5px] font-semibold uppercase"
              style={{
                letterSpacing: '0.12em',
                borderColor: search.hasIssues ? 'rgba(var(--err-rgb), 0.45)' : 'var(--bd)',
                background: search.hasIssues ? 'rgba(var(--err-rgb), 0.1)' : 'transparent',
                color: search.hasIssues ? 'var(--err-soft)' : 'var(--tx3)',
              }}
            >
              Has open issues
            </button>
          </div>
        </div>

        {committedTooShort ? null : isError ? (
          <ErrorState error={error} subject="leagues" onRetry={() => void refetch()} />
        ) : (
          <>
            <DataTable
              label="Leagues"
              columns={columns}
              rows={data?.rows ?? []}
              rowKey={(league) => league.id}
              isLoading={isPending}
              sort={sort}
              onSortChange={(next) =>
                setSearch({ sort: next.columnId, direction: next.direction })
              }
              selection={{
                selected,
                onChange: setSelected,
                // Safe operations only, per the brief.
                actions: <DangerButton tier={1}>Export selected</DangerButton>,
              }}
              emptyState={
                <EmptyState
                  title="No leagues match"
                  description="Try clearing a filter, or search by invite code."
                />
              }
            />

            {data && data.total !== null && data.total > data.pageSize && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(page) => setSearch({ page })}
              />
            )}
          </>
        )}
      </div>
    </RequireCapability>
  );
}

function IssueBadge({ issue }: { issue: LeagueIssue }) {
  const meta = ISSUE_LABELS[issue];
  const isBad = meta.severity === 'bad';

  return (
    <span
      className="font-head inline-flex items-center rounded px-1.5 py-px text-[9.5px] font-semibold uppercase"
      style={{
        letterSpacing: '0.1em',
        background: isBad ? 'rgba(var(--err-rgb), 0.14)' : 'rgba(var(--warn-rgb), 0.14)',
        color: isBad ? 'var(--err-soft)' : 'var(--warn)',
      }}
    >
      {meta.label}
    </span>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="border-bd bg-bg-alt text-tx2 h-9 rounded-lg border px-2 text-[12.5px] capitalize outline-none"
      >
        <option value="">{label}: any</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="mesh-mono text-tx3 text-[11.5px]">
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Prev
        </button>
        <span className="mesh-mono text-tx3 text-[11.5px]">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
